/**
 * <PasswordRequirements> — live checklist of the password policy.
 *
 * The five rules mirror `bc_shared.auth.validate_password_strength` and its
 * client-side mirror `apps/buyer-portal/frontend/src/lib/passwordPolicy.js`
 * exactly, in the same order:
 *   - at least 7 characters
 *   - 1 uppercase letter
 *   - 1 lowercase letter
 *   - 1 digit
 *   - 1 special character
 *
 * Rendered next to the password field (and next to the submit button, via
 * <FormError>) so a user who taps REGISTER on a weak password can see what is
 * missing without scrolling. This is UX feedback, not the security boundary —
 * the server enforces the same rules.
 *
 * Deliberately dependency-free: no icon library, no toast library. The check /
 * open-circle glyphs are literal characters, hidden from screen readers; each
 * row instead carries an aria-label ending in "met" / "not met".
 *
 * Usage
 * -----
 *
 *     <PasswordRequirements password={password} className="mt-2" />
 *
 * `PASSWORD_RULES` is exported so callers can run the same predicates for
 * gating a submit button:
 *
 *     const ok = PASSWORD_RULES.every((r) => r.test(password));
 */

import React from 'react';

/**
 * @typedef {object} PasswordRule
 * @property {string} id                        — Stable key, e.g. 'length'.
 * @property {string} label                     — Human-readable requirement.
 * @property {(password: string) => boolean} test
 */

/** @type {PasswordRule[]} */
export const PASSWORD_RULES = [
  { id: 'length', label: 'At least 7 characters', test: (v) => (v || '').length >= 7 },
  { id: 'uppercase', label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v || '') },
  { id: 'lowercase', label: 'One lowercase letter', test: (v) => /[a-z]/.test(v || '') },
  { id: 'digit', label: 'One digit', test: (v) => /\d/.test(v || '') },
  {
    id: 'special',
    label: 'One special character',
    test: (v) => /[^a-zA-Z0-9]/.test(v || ''),
  },
];

/**
 * @typedef {object} PasswordRequirementsProps
 * @property {string} [password]   — The current password value.
 * @property {string} [className]  — Appended last, so callers can add spacing.
 */

export function PasswordRequirements({ password, className = '' }) {
  const value = password || '';

  return (
    <ul
      // Deliberately "off". A live region here would re-announce all five
      // rules on EVERY keystroke into the password field, which is unusable
      // with a screen reader. The assertive channel is <FormError>, which
      // announces the whole policy failure once, on submit. Each <li> still
      // carries a "met" / "not met" aria-label, so a user who navigates into
      // the list on demand gets the current state.
      aria-live="off"
      className={`text-xs space-y-1 ${className}`}
    >
      {PASSWORD_RULES.map((rule) => {
        const passed = rule.test(value);
        return (
          <li
            key={rule.id}
            aria-label={`${rule.label} — ${passed ? 'met' : 'not met'}`}
            className={`break-words ${passed ? 'text-green-700' : 'text-gray-500'}`}
          >
            <span aria-hidden="true">{passed ? '✓ ' : '○ '}</span>
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}
